import { ensureCsrfToken, withCsrfInit } from '../csrf';
import { authFetch } from '../utils/authFetch';

import { assertOk, assertOkJson } from './response';
import { requestAuthedJson } from './http';

async function requestWithCsrfRetry(url: string, buildInit: () => Promise<RequestInit>): Promise<Response> {
  let init = await buildInit();
  let response = await authFetch(url, init);
  if (response.status === 403) {
    await ensureCsrfToken(true);
    init = await buildInit();
    response = await authFetch(url, init);
  }
  return response;
}

export interface HrRealtimeStats {
  unreadMessages: number;
  pendingRequests: number;
}

export interface HrEmployeesPayload<TEmployee = unknown> {
  employees?: TEmployee[];
  total?: number;
  [key: string]: unknown;
}

export async function fetchHrJson<T>(path: string, fallback: string): Promise<T> {
  return requestAuthedJson<T>(path, fallback, { credentials: 'include' });
}

export async function fetchHrRealtimeStats(): Promise<HrRealtimeStats> {
  const [messagesRes, requestsRes] = await Promise.all([
    authFetch('/hr/api/messages/stats'),
    authFetch('/hr/api/requests/stats'),
  ]);

  if (!messagesRes.ok || !requestsRes.ok) {
    throw new Error('Failed to fetch HR realtime stats');
  }

  const [messagesData, requestsData] = await Promise.all([
    messagesRes.json() as Promise<{ unread_messages?: number }>,
    requestsRes.json() as Promise<{ pending_requests?: number }>,
  ]);

  return {
    unreadMessages: messagesData.unread_messages ?? 0,
    pendingRequests: requestsData.pending_requests ?? 0,
  };
}

export async function fetchHrDashboardSafe(): Promise<unknown | null> {
  const response = await authFetch('/hr/api/dashboard');
  if (!response.ok) {
    return null;
  }
  return response.json();
}

export async function fetchHrEmployeesPageSafe(): Promise<HrEmployeesPayload> {
  const response = await authFetch('/hr/api/employees?page=1&page_size=50');
  if (!response.ok) {
    return { employees: [], total: 0 };
  }
  return (await response.json()) as HrEmployeesPayload;
}

export async function fetchHrEmployeesList<TEmployee = unknown>(): Promise<HrEmployeesPayload<TEmployee>> {
  const response = await authFetch('/hr/api/employees');
  return assertOkJson<HrEmployeesPayload<TEmployee>>(response, 'Failed to load employees');
}

export async function fetchHrApprovedEmployees<TEmployee = unknown>(
  limit = 500,
): Promise<HrEmployeesPayload<TEmployee>> {
  const response = await authFetch(`/hr/api/employees?limit=${limit}&approval_status=approved`);
  return assertOkJson<HrEmployeesPayload<TEmployee>>(response, 'Failed to load employees');
}

export interface HrSchedule {
  grace_minutes?: number; effective_from?: string; effective_to?: string;
  id?: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  shift_type?: string;
}

export interface HrEmployeeSchedulesResponse {
  employee_name?: string;
  schedules: HrSchedule[];
}

export async function fetchHrEmployeeSchedules(employeeId: number): Promise<HrEmployeeSchedulesResponse> {
  const response = await authFetch(`/hr/api/employees/${employeeId}/schedules`);
  return assertOkJson<HrEmployeeSchedulesResponse>(response, 'Failed to load schedules');
}

export async function saveHrEmployeeSchedules(
  employeeId: number,
  schedules: HrSchedule[],
  effective_from?: string,
  reason?: string,
): Promise<{ message?: string; [key: string]: unknown }> {
  const csrfToken = await ensureCsrfToken();
  const response = await authFetch(`/hr/api/employees/${employeeId}/schedules/bulk`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    body: JSON.stringify({ schedules, effective_from, reason }),
  });
  return assertOkJson<{ message?: string; [key: string]: unknown }>(response, 'Failed to save schedules');
}

export interface HrGroupRatesResponse<TInstructor = unknown> {
  instructors: TInstructor[];
}

export async function fetchHrGroupRates<TInstructor = unknown>(): Promise<HrGroupRatesResponse<TInstructor>> {
  const response = await authFetch('/hr/api/group-rates');
  return assertOkJson<HrGroupRatesResponse<TInstructor>>(response, 'Failed to fetch data');
}

export async function saveHrGroupRate(groupId: number, instructorHourlyRate: number | null): Promise<void> {
  const response = await authFetch(`/hr/api/group-rates/${groupId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ instructor_hourly_rate: instructorHourlyRate }),
  });
  await assertOk(response, 'Failed to save');
}

export async function fetchHrInstructorPayrollSummary<TSummary = unknown>(
  year: number,
  month: number,
): Promise<TSummary> {
  const response = await authFetch(`/hr/api/instructor-payroll/summary?year=${year}&month=${month}`);
  return assertOkJson<TSummary>(response, 'Failed to load payroll data');
}

export async function fetchHrDashboardSummary<TSummary = unknown>(
  year: number,
  month: number,
): Promise<TSummary> {
  const response = await authFetch(
    `/hr/api/dashboard_summary?year=${year}&month=${month}`,
    await withCsrfInit({ credentials: 'include' }),
  );
  return assertOkJson<TSummary>(response, 'Failed to load dashboard stats');
}

export async function setHrEmployeeApproval(
  employeeId: number,
  approvalStatus: 'approved' | 'rejected',
  csrfToken?: string,
): Promise<void> {
  const formData = new FormData();
  formData.append('approval_status', approvalStatus);

  const headers: HeadersInit = {};
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const response = await authFetch(`/hr/api/employees/${employeeId}/approval`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers,
  });

  await assertOk(response, `Failed to ${approvalStatus === 'approved' ? 'approve' : 'reject'} employee`);
}

export async function updateHrEmployee(
  employeeId: number,
  formData: FormData,
  headers?: HeadersInit,
): Promise<void> {
  const response = await authFetch(`/hr/api/employees/${employeeId}`, {
    method: 'POST',
    body: formData,
    headers,
  });
  await assertOk(response, 'Failed to update employee');
}

export async function createHrEmployee(formData: FormData, headers?: HeadersInit): Promise<void> {
  const response = await authFetch('/hr/api/employees', {
    method: 'POST',
    body: formData,
    headers,
  });
  await assertOk(response, 'Failed to create employee');
}

export async function fetchHrEmployeeDetail<TEmployee = unknown>(employeeId: number): Promise<TEmployee> {
  const response = await authFetch(`/hr/api/employees/${employeeId}`);
  return assertOkJson<TEmployee>(response, 'Failed to load employee details');
}

export interface HrCommentsResponse<TComment = unknown> {
  comments?: TComment[];
}

export async function fetchHrEmployeeComments<TComment = unknown>(employeeId: number): Promise<HrCommentsResponse<TComment>> {
  const response = await authFetch(`/hr/api/employees/${employeeId}/comments`);
  return assertOkJson<HrCommentsResponse<TComment>>(response, 'Failed to load comments');
}

export async function createHrEmployeeComment<TComment = unknown>(
  employeeId: number,
  body: string,
): Promise<{ comment?: TComment }> {
  const formData = new FormData();
  formData.append('body', body);

  const response = await authFetch(
    `/hr/api/employees/${employeeId}/comments`,
    await withCsrfInit({ method: 'POST', body: formData }),
  );
  return assertOkJson<{ comment?: TComment }>(response, 'Add failed');
}

export async function updateHrEmployeeComment<TComment = unknown>(
  employeeId: number,
  commentId: number,
  body: string,
): Promise<{ comment?: TComment }> {
  const formData = new FormData();
  formData.append('body', body);

  const response = await authFetch(
    `/hr/api/employees/${employeeId}/comments/${commentId}`,
    await withCsrfInit({ method: 'PUT', body: formData }),
  );
  return assertOkJson<{ comment?: TComment }>(response, 'Update failed');
}

export async function deleteHrEmployeeComment(employeeId: number, commentId: number): Promise<void> {
  const response = await authFetch(
    `/hr/api/employees/${employeeId}/comments/${commentId}`,
    await withCsrfInit({ method: 'DELETE' }),
  );
  await assertOk(response, 'Delete failed');
}

export interface HrGeofence {
  id: number;
  branch?: string;
  name?: string;
  center_lat?: number;
  center_lng?: number;
  radius_meters?: number;
  is_active?: boolean;
}

export interface HrGeofenceResponse {
  geofences?: HrGeofence[];
}

export async function fetchHrGeofences(): Promise<HrGeofenceResponse> {
  const response = await authFetch('/hr/api/geofences', { credentials: 'include' });
  return assertOkJson<HrGeofenceResponse>(response, 'Failed to load geofences');
}

export interface SaveHrGeofencePayload {
  name: string;
  branch: string;
  center_lat: number;
  center_lng: number;
  radius_meters: number;
  is_active: boolean;
}

export async function saveHrGeofence(
  geofenceId: number | null,
  payload: SaveHrGeofencePayload,
): Promise<void> {
  const url = geofenceId ? `/hr/api/geofences/${geofenceId}` : '/hr/api/geofences';
  const method = geofenceId ? 'PUT' : 'POST';

  const response = await requestWithCsrfRetry(url, () => withCsrfInit({
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  }));

  await assertOk(response, 'Failed to save geofence');
}

export async function deleteHrGeofence(geofenceId: number): Promise<void> {
  const response = await requestWithCsrfRetry(
    `/hr/api/geofences/${geofenceId}`,
    () => withCsrfInit({
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    }),
  );

  await assertOk(response, 'Failed to delete geofence');
}

export async function saveHrAttendanceNotes(dayId: number, notes: string): Promise<void> {
  const response = await requestWithCsrfRetry('/hr/api/attendance/update_notes', () => withCsrfInit({
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ day_id: dayId, notes }),
  }));

  await assertOk(response, 'Failed to save notes');
}

export async function fetchHrAttendanceDetail<TDetail = unknown>(
  employeeId: number,
  year: number,
  month: number,
): Promise<TDetail> {
  const response = await authFetch(`/hr/api/attendance/${employeeId}?year=${year}&month=${month}`, await withCsrfInit({}));
  return assertOkJson<TDetail>(response, `HTTP ${response.status}`);
}

export async function fetchHrPayrollDetail<TDetail = unknown>(
  employeeId: number,
  year: number,
  month: number,
): Promise<TDetail> {
  const response = await authFetch(`/hr/api/payroll/${employeeId}?year=${year}&month=${month}`, await withCsrfInit({}));
  return assertOkJson<TDetail>(response, `HTTP ${response.status}`);
}

export async function fetchHrPayrollSummary<TSummary = unknown>(year: number, month: number): Promise<TSummary> {
  const response = await authFetch(`/hr/api/payroll/summary?year=${year}&month=${month}`);
  return assertOkJson<TSummary>(response, `HTTP ${response.status}`);
}

export async function prepareHrPayroll<TPayload = unknown>(employeeId: number, year: number, month: number, reason: string): Promise<TPayload> {
  const formData = new FormData();
  formData.set('year', String(year));
  formData.set('month', String(month));
  formData.set('reason', reason);
  const response = await authFetch(`/hr/api/payroll/${employeeId}/prepare`, await withCsrfInit({ method: 'POST', body: formData }));
  return assertOkJson<TPayload>(response, 'Failed to prepare payroll draft');
}

export async function addHrPayrollItem(
  employeeId: number,
  year: number,
  month: number,
  itemType: string,
  label: string,
  amount: number,
): Promise<void> {
  const formData = new FormData();
  formData.set('year', String(year));
  formData.set('month', String(month));
  formData.set('item_type', itemType);
  formData.set('label', label);
  formData.set('amount', String(amount));

  const response = await authFetch(`/hr/api/payroll/${employeeId}/item`, await withCsrfInit({
    method: 'POST',
    body: formData,
  }));

  await assertOk(response, `HTTP ${response.status}`);
}

export async function deleteHrPayrollItem(
  employeeId: number,
  itemId: number,
  year: number,
  month: number,
): Promise<void> {
  const formData = new FormData();
  formData.set('year', String(year));
  formData.set('month', String(month));

  const response = await authFetch(
    `/hr/api/payroll/${employeeId}/item/${itemId}/delete`,
    await withCsrfInit({
      method: 'POST',
      body: formData,
    }),
  );

  await assertOk(response, `HTTP ${response.status}`);
}

export async function finalizeHrPayroll(employeeId: number, year: number, month: number): Promise<void> {
  const formData = new FormData();
  formData.set('year', String(year));
  formData.set('month', String(month));

  const response = await authFetch(`/hr/api/payroll/${employeeId}/finalize`, await withCsrfInit({
    method: 'POST',
    body: formData,
  }));

  await assertOk(response, `HTTP ${response.status}`);
}

export async function downloadHrAttendanceReportBlob(year: number, month: number): Promise<Blob> {
  const response = await authFetch(`/hr/api/export/attendance?year=${year}&month=${month}`, {
    ...await withCsrfInit({}),
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('Export failed');
  }

  return response.blob();
}

export async function downloadHrPayrollReportBlob(year: number, month: number): Promise<Blob> {
  const response = await authFetch(`/hr/api/export/payroll?year=${year}&month=${month}`, {
    ...await withCsrfInit({}),
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('Export failed');
  }

  return response.blob();
}

export async function fetchHrMessages<TPayload = unknown>(unreadOnly: boolean): Promise<TPayload> {
  const params = new URLSearchParams();
  if (unreadOnly) params.append('unread_only', 'true');
  const query = params.toString() ? `?${params.toString()}` : '';

  const response = await authFetch(`/hr/api/messages${query}`);
  return assertOkJson<TPayload>(response, `HTTP ${response.status}`);
}

export async function fetchHrMessageStats<TStats = unknown>(): Promise<TStats> {
  const response = await authFetch('/hr/api/messages/stats');
  return assertOkJson<TStats>(response, `HTTP ${response.status}`);
}

export async function markHrMessageAsRead(messageId: number): Promise<void> {
  const response = await authFetch(
    `/hr/api/messages/${messageId}/mark-read`,
    await withCsrfInit({ method: 'POST' }),
  );
  await assertOk(response, `HTTP ${response.status}`);
}

export async function sendHrMessageResponse(
  messageId: number,
  subject: string,
  content: string,
): Promise<void> {
  const formData = new FormData();
  formData.append('subject', subject);
  formData.append('content', content);

  const response = await authFetch(
    `/hr/api/messages/${messageId}/respond`,
    await withCsrfInit({
      method: 'POST',
      body: formData,
    }),
  );
  await assertOk(response, `HTTP ${response.status}`);
}

export async function fetchHrRequests<TPayload = unknown>(
  statusFilter: string,
  typeFilter: string,
): Promise<TPayload> {
  const params = new URLSearchParams();
  if (statusFilter) params.append('status', statusFilter);
  if (typeFilter) params.append('request_type', typeFilter);
  const query = params.toString() ? `?${params.toString()}` : '';

  const response = await authFetch(`/hr/api/requests${query}`);
  return assertOkJson<TPayload>(response, `HTTP ${response.status}`);
}

export async function fetchHrRequestStats<TStats = unknown>(): Promise<TStats> {
  const response = await authFetch('/hr/api/requests/stats');
  return assertOkJson<TStats>(response, `HTTP ${response.status}`);
}

export async function respondHrRequest(
  requestId: number,
  action: 'approve' | 'deny' | 'defer',
  feedback: string,
): Promise<void> {
  const formData = new FormData();
  formData.append('action', action);
  formData.append('feedback', feedback);

  const response = await authFetch(
    `/hr/api/requests/${requestId}/respond`,
    await withCsrfInit({
      method: 'POST',
      body: formData,
    }),
  );

  await assertOk(response, `HTTP ${response.status}`);
}

export async function hrIntegrityRequest<T>(path: string, body?: object): Promise<T> {
  const response = await authFetch(path, body ? await withCsrfInit({method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}) : {});
  const payload = await response.json();
  if (!response.ok) throw new Error(typeof payload.detail === 'object' ? `${payload.detail.message}: ${(payload.detail.missing || []).join(', ')}` : payload.detail || payload.error || 'HR request failed');
  return payload as T;
}
